import { css } from '@emotion/react';
import Select from 'react-select';
import { useState } from 'react';
import { getColor } from '../foundations';
import { wplToken } from '@atlassiansox/wpl-token';

export const dropdownSelectorContainerStyle = css`
  display: inline-flex;
  flex-direction: column;
  width: 100%;
`;

export const labelStyle = css`
  color: ${getColor('n500')};
  font-size: 0.875rem;
  font-weight: 25rem;
  line-height: 1.125rem;
  letter-spacing: 0rem;
  margin-bottom: ${wplToken('space.050')};
  text-align: left;
`;

interface Option {
  value: string;
  label: string;
}

export interface DropdownSelectorProps {
  options: Option[];
  name: string;
  label: string;
  className?: string;
  defaultValue?: Option;
}

export const DropdownSelector = ({
  options,
  name,
  label,
  defaultValue,
  className
}: DropdownSelectorProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className={className} css={dropdownSelectorContainerStyle}>
      <label css={labelStyle} htmlFor={name}>
        {label}
      </label>
      <Select
        defaultValue={defaultValue || 'Select...'}
        options={options}
        unstyled={true}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        styles={{
          container: (provided) => ({
            ...provided,
            appearance: 'none',
            flexDirection: 'row',
            fontSize: '1.125rem',
            fontWeight: '25rem',
            letterSpacing: '0rem',
            lineHeight: '1.625rem',
            textAlign: 'left'
          }),

          singleValue: (provided) => ({
            ...provided,
            marginLeft: wplToken('space.025'),
            marginRight: wplToken('space.025')
          }),

          placeholder: (provided) => ({
            ...provided,
            marginLeft: wplToken('space.025'),
            marginRight: wplToken('space.025')
          }),

          valueContainer: (provided) => ({
            ...provided,
            paddingLeft: wplToken('space.075'),
            paddingRight: wplToken('space.075'),
            paddingTop: wplToken('space.025'),
            paddingBottom: wplToken('space.025')
          }),

          menu: (provided) => ({
            ...provided,
            marginTop: wplToken('space.100'),
            boxShadow: '0px 3px 5px 0px #091E4233',
            background: getColor('n0'),
            padding: `${wplToken('space.100')} 0 ${wplToken('space.100')} 0`,
            borderRadius: wplToken('space.050')
          }),

          control: (provided, state) => ({
            ...provided,
            'background': getColor('n0'),
            'color': getColor('n800'),
            'border': state.isFocused
              ? `2px solid ${getColor('b100')}`
              : `2px solid ${getColor('n30')}`,
            'borderRadius': wplToken('space.050'),
            '&:hover': {
              background: isMenuOpen ? undefined : getColor('n20')
            }
          }),

          dropdownIndicator: (provided, state) => ({
            ...provided,
            transition: 'all .2s ease',
            transform: state.selectProps.menuIsOpen
              ? 'rotate(180deg)'
              : undefined
          }),

          indicatorsContainer: (provided) => ({
            ...provided,
            paddingTop: wplToken('space.075'),
            paddingBottom: wplToken('space.075'),
            paddingLeft: wplToken('space.025'),
            paddingRight: wplToken('space.075')
          }),

          option: (provided, state) => ({
            ...provided,
            'paddingLeft': '0.625rem',
            'paddingRight': '0.625rem',
            'whiteSpace': 'pre-wrap',
            'overflowWrap': 'break-word',
            'background': state.isSelected
              ? '#b3d4ff'
              : state.isFocused
              ? getColor('n20')
              : getColor('n0'),
            'color': state.isSelected ? '#0c66e4' : getColor('n800'),
            '&:hover': {
              background: state.isSelected ? '#88b8f5' : getColor('n20')
            },
            '&:active': {
              background: getColor('n30')
            }
          }),

          input: (provided) => ({
            ...provided,
            color: 'transparent'
          })
        }}
      />
    </div>
  );
};
